/**
 * PriceHistogramSlider
 *
 * Diamond Architecture (Pure BFF): Histogram chart + dual-thumb range slider for price filtering.
 * Uses serializable types from server actions.
 *
 * Features:
 * - Histogram bars showing price distribution
 * - Selected range highlighted in brand color
 * - Dual-thumb slider for min/max selection
 * - Non-linear scale from the Rust pricing SSOT (`pricing_buckets`) — the SAME
 *   compiled crate the backend uses to build the histogram, so slider positions
 *   and bucket edges can never drift
 * - Price labels showing formatted values
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { HistogramBucketData } from '@/shared/lib/types/suggestions';
import { PRICING_MAX_CENTS, PRICING_MIN_CENTS } from '@letsgethome/lib/constants';
import { pricingPositionToPrice, pricingPriceToPosition } from '@letsgethome/lib/wasm';
import { plaintextString } from '@letsgethome/types/branded';
import { useJsOnlyInert } from '@letsgethome/ui/hooks/use-hydrated';
import { cn } from '@letsgethome/ui/lib/utils';
import { useCallback, useMemo, useRef } from 'react';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface PriceHistogramSliderProps {
  /** Histogram buckets from backend (50 buckets, height 0-100) */
  buckets: HistogramBucketData[];
  /** Rent vs sale — selects the SSOT price scale (rent/sale have different ceilings) */
  isRent: boolean;
  /** Current min price in cents (null = no minimum) */
  minPrice: bigint | null;
  /** Current max price in cents (null = no maximum) */
  maxPrice: bigint | null;
  /** Callback when range changes */
  onChange: (min: bigint | null, max: bigint | null) => void;
  /** Format price for display */
  formatPrice: (cents: bigint) => string;
}

// ----------------------------------------------------------------------------
// Helpers: seam between bigint view-model cents and the wasm SSOT (f64 cents).
// Cents are exact in doubles well past the sale ceiling ($20M = 2e9 cents),
// so the Number()/BigInt() round-trips below are lossless.
// ----------------------------------------------------------------------------

/** Price (cents) → slider position (0-100) on the SSOT non-linear scale. */
function priceToPosition(isRent: boolean, priceCents: bigint): number {
  return pricingPriceToPosition(isRent, Number(priceCents));
}

/** Slider position (0-100) → price (cents) on the SSOT non-linear scale. */
function positionToPrice(isRent: boolean, position: number): bigint {
  return BigInt(Math.round(pricingPositionToPrice(isRent, position)));
}

// ----------------------------------------------------------------------------
// Histogram Chart
// ----------------------------------------------------------------------------

function HistogramChart({
  buckets,
  minPrice,
  maxPrice,
  floorCents,
  ceilingCents,
}: {
  buckets: HistogramBucketData[];
  minPrice: bigint | null;
  maxPrice: bigint | null;
  floorCents: bigint;
  ceilingCents: bigint;
}) {
  const effectiveMin = minPrice ?? floorCents;
  const effectiveMax = maxPrice ?? ceilingCents;

  return (
    <div className="flex items-end gap-px h-20">
      {buckets.map((bucket) => {
        const bucketMin = BigInt(bucket.minPriceCents);
        const bucketMax = BigInt(bucket.maxPriceCents);
        const isSelected = bucketMin >= effectiveMin && bucketMax <= effectiveMax;

        return (
          <div
            key={bucket.bucketNumber}
            className={cn(
              'flex-1 rounded-t-sm transition-colors duration-150',
              isSelected ? 'bg-primary' : 'bg-primary/20'
            )}
            style={{ height: `${Math.max(bucket.height, 2)}%` }}
          />
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Dual-Thumb Slider
// ----------------------------------------------------------------------------

/** Minimum separation between the two thumbs, in track percent. ONE constant:
 *  the pointer-drag clamps and the keyboard clamps must agree about what a legal
 *  position is, and two literals would eventually drift. */
const THUMB_GAP = 5;
/** Keyboard granularity: a single arrow press, and a Page{Up,Down} jump. */
const STEP = 1;
const PAGE_STEP = 10;

function DualThumbSlider({
  minPosition,
  maxPosition,
  onMinChange,
  onMaxChange,
  minValueText,
  maxValueText,
}: {
  minPosition: number;
  maxPosition: number;
  onMinChange: (pos: number) => void;
  onMaxChange: (pos: number) => void;
  /** Formatted price for `aria-valuetext` — a screen reader should hear the
   *  PRICE, not the 0-100 track position the thumb happens to sit at. */
  minValueText: string;
  maxValueText: string;
}) {
  // Drag thumbs are dead until React attaches the pointer handlers; `inert` is
  // the <div> analogue of `disabled`.
  const thumbInert = useJsOnlyInert();
  // The accessible NAME comes from the existing translated `min`/`max` strings
  // (top-level catalog keys — `useTranslations('')` is the documented no-prefix
  // form). They already ship Chinese values for android, so giving them a
  // `web_public` surface needed no new translation: the ui-strings completeness
  // gate requires zh-CN + zh-HK for every new key, and those can only be
  // authored in the Console, not in this repo.
  const t = useTranslations('');
  const trackRef = useRef<HTMLDivElement>(null);

  const getPositionFromEvent = useCallback((clientX: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(100, (x / rect.width) * 100));
  }, []);

  const handleMinDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        const newPos = getPositionFromEvent(moveEvent.clientX);
        // Keep min at least 5% below max
        if (newPos < maxPosition - THUMB_GAP) {
          onMinChange(newPos);
        }
      };

      const onUp = () => {
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
    },
    [getPositionFromEvent, maxPosition, onMinChange]
  );

  const handleMaxDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        const newPos = getPositionFromEvent(moveEvent.clientX);
        // Keep max at least 5% above min
        if (newPos > minPosition + THUMB_GAP) {
          onMaxChange(newPos);
        }
      };

      const onUp = () => {
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
    },
    [getPositionFromEvent, minPosition, onMaxChange]
  );

  // KEYBOARD. Until now these thumbs were pointer-only: no role, no tabIndex,
  // no aria — so the price filter could not be set at all without a pointer and
  // was invisible to assistive tech (#6009). The clamps below are the SAME ones
  // the drag handlers enforce (a THUMB_GAP between thumbs), so keyboard and
  // pointer cannot disagree about which positions are legal.
  const nudge = (key: string): number => {
    switch (key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        return -STEP;
      case 'ArrowRight':
      case 'ArrowUp':
        return STEP;
      case 'PageDown':
        return -PAGE_STEP;
      case 'PageUp':
        return PAGE_STEP;
      default:
        return 0;
    }
  };

  const handleMinKey = useCallback(
    (e: React.KeyboardEvent) => {
      const delta = nudge(e.key);
      if (delta === 0 && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const target =
        e.key === 'Home' ? 0 : e.key === 'End' ? maxPosition - THUMB_GAP : minPosition + delta;
      onMinChange(Math.max(0, Math.min(target, maxPosition - THUMB_GAP)));
    },
    [minPosition, maxPosition, onMinChange]
  );

  const handleMaxKey = useCallback(
    (e: React.KeyboardEvent) => {
      const delta = nudge(e.key);
      if (delta === 0 && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const target =
        e.key === 'End' ? 100 : e.key === 'Home' ? minPosition + THUMB_GAP : maxPosition + delta;
      onMaxChange(Math.min(100, Math.max(target, minPosition + THUMB_GAP)));
    },
    [minPosition, maxPosition, onMaxChange]
  );

  return (
    <div ref={trackRef} className="relative h-6">
      {/* Track background */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-muted rounded-full" />

      {/* Selected range */}
      <div
        className="absolute top-1/2 -translate-y-1/2 h-1 bg-primary rounded-full"
        style={{
          left: `${minPosition}%`,
          right: `${100 - maxPosition}%`,
        }}
      />

      {/* Min thumb */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-7 h-7 bg-background rounded-full cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        style={{
          left: `${minPosition}%`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
        role="slider"
        tabIndex={0}
        aria-label={t('min')}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, maxPosition - THUMB_GAP)}
        aria-valuenow={minPosition}
        aria-valuetext={minValueText}
        inert={thumbInert}
        onKeyDown={handleMinKey}
        onPointerDown={handleMinDrag}
      />

      {/* Max thumb */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-7 h-7 bg-background rounded-full cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        style={{
          left: `${maxPosition}%`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
        role="slider"
        tabIndex={0}
        aria-label={t('max')}
        aria-valuemin={Math.min(100, minPosition + THUMB_GAP)}
        aria-valuemax={100}
        aria-valuenow={maxPosition}
        aria-valuetext={maxValueText}
        inert={thumbInert}
        onKeyDown={handleMaxKey}
        onPointerDown={handleMaxDrag}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export function PriceHistogramSlider({
  buckets,
  isRent,
  minPrice,
  maxPrice,
  onChange,
  formatPrice,
}: PriceHistogramSliderProps) {
  // Scale floor/ceiling from the SSOT (bigint at the view-model seam)
  const minCents = useMemo(() => BigInt(PRICING_MIN_CENTS[isRent ? 'rent' : 'sale']), [isRent]);
  const maxCents = useMemo(() => BigInt(PRICING_MAX_CENTS[isRent ? 'rent' : 'sale']), [isRent]);

  // Calculate slider positions from prices
  const minPosition = useMemo(() => {
    const price = minPrice ?? minCents;
    return priceToPosition(isRent, price);
  }, [minPrice, minCents, isRent]);

  const maxPosition = useMemo(() => {
    const price = maxPrice ?? maxCents;
    return priceToPosition(isRent, price);
  }, [maxPrice, maxCents, isRent]);

  // Handle slider changes
  const handleMinChange = useCallback(
    (pos: number) => {
      const newPrice = positionToPrice(isRent, pos);
      // If at minimum, treat as "no minimum"
      const finalPrice = newPrice <= minCents ? null : newPrice;
      onChange(finalPrice, maxPrice);
    },
    [isRent, minCents, maxPrice, onChange]
  );

  const handleMaxChange = useCallback(
    (pos: number) => {
      const newPrice = positionToPrice(isRent, pos);
      // If at maximum, treat as "no maximum"
      const finalPrice = newPrice >= maxCents ? null : newPrice;
      onChange(minPrice, finalPrice);
    },
    [isRent, maxCents, minPrice, onChange]
  );

  // Format display values
  const displayMin = plaintextString(formatPrice(minPrice ?? minCents));
  const displayMax = maxPrice
    ? plaintextString(formatPrice(maxPrice))
    : plaintextString(`${formatPrice(maxCents)}+`);

  return (
    <div>
      {/* Histogram + Slider stacked: histogram sits directly on the slider track */}
      <div className="relative">
        <div className="pb-3">
          <HistogramChart
            buckets={buckets}
            minPrice={minPrice}
            maxPrice={maxPrice}
            floorCents={minCents}
            ceilingCents={maxCents}
          />
        </div>
        <div className="absolute bottom-0 inset-x-0">
          <DualThumbSlider
            minPosition={minPosition}
            maxPosition={maxPosition}
            onMinChange={handleMinChange}
            onMaxChange={handleMaxChange}
            minValueText={displayMin}
            maxValueText={displayMax}
          />
        </div>
      </div>

      {/* Price labels */}
      <div className="flex justify-between text-sm text-muted-foreground">
        <span>{displayMin}</span>
        <span>{displayMax}</span>
      </div>
    </div>
  );
}
